import { Injectable } from '@angular/core';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { TaskAPIService } from '../api/task-api.service';
import { TasksMessages } from '../locale/tasks.messages';
import { CreateTaskCommentDto } from '../model/create-task-comment.dto';
import { TaskComment } from '../model/task-comment.model';
import { TaskStatus } from '../model/task-status.enum';
import { UpdateTaskStatusDto } from '../model/update-task-status.dto';
import { Task } from '../model/task.model';
import { CreateTaskDto } from '../model/create-task.dto';
import { TaskAlignmentType } from '../model/task-alignment-type.enum';
import { Observable, firstValueFrom } from 'rxjs';
import { TaskActivity } from '../model/task-activity.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

@Injectable()
export class TaskBusinessService {

  // taskId -> the write currently in flight for that task. A duplicate call while one is in flight
  // is coalesced onto the same promise (see InFlightRegistry) rather than rejected. A single map guards
  // every write (update, status, comment, archive, ...), matching the original combined guard.
  private static _modifying: InFlightRegistry;

  public get archiving(): number[] {
    return TaskBusinessService._modifying.keys<number>();
  }

  public get updating(): number[] {
    return TaskBusinessService._modifying.keys<number>();
  }

  public get modifying(): number[] {
    return TaskBusinessService._modifying.keys<number>();
  }

  constructor(
    private taskAPIService: TaskAPIService,
    private notifyUtils: NotifyUtils
  ) {
    TaskBusinessService._modifying = new InFlightRegistry();
  }

  createNewTask(createTaskDto: CreateTaskDto): Promise<Task> {
    return firstValueFrom(this.taskAPIService.create(createTaskDto))
      .then(t => {
        this.notifyUtils.notify(TasksMessages.SUCCESS_TASK_CREATED);
        return t;
      });
  }

  updateExistingTask(taskId: number, createTaskDto: CreateTaskDto): Promise<Task> {
    return TaskBusinessService._modifying.run(taskId, () =>
      firstValueFrom(this.taskAPIService.update(taskId, createTaskDto))
        .then(t => {
          this.notifyUtils.notify(TasksMessages.SUCCESS_TASK_UPDATED);
          return t;
        })
    );
  }

  removeTaskAlignment(task: Task): Promise<Task> {
    const createTaskDto: CreateTaskDto = {
      title: task.title,
      description: task.description,
      dueDate: task.dueDate,
      alignmentType: TaskAlignmentType.TODO,
      alignmentId: null,
      ownerIds: task.owners.map(o => o.id),
      createdFrom: task.createdFrom
    };

    return TaskBusinessService._modifying.run(task.id, () =>
      firstValueFrom(this.taskAPIService.update(task.id, createTaskDto))
        .then(t => {
          this.notifyUtils.notify(TasksMessages.SUCCESS_UNLINK_TASK);
          return t;
        })
    );
  }

  updateStatusOfTask(taskId: number, newStatus: TaskStatus): Promise<Task> {
    const updateTaskStatusDto: UpdateTaskStatusDto = {
      taskStatus: newStatus
    };

    return TaskBusinessService._modifying.run(taskId, () =>
      firstValueFrom(this.taskAPIService.updateStatus(taskId, updateTaskStatusDto))
        .then(t => {
          this.notifyUtils.notify(TasksMessages.SUCCESS_TASK_UPDATED);
          return t;
        })
    );
  }

  archiveTask(taskId: number): Promise<Task> {
    return TaskBusinessService._modifying.run(taskId, () =>
      firstValueFrom(this.taskAPIService.archive(taskId))
        .then(t => {
          this.notifyUtils.notify(TasksMessages.SUCCESS_ARCHIVE_TASK);
          return t;
        })
    );
  }

  commentOnTask(taskId: number, comment: string): Promise<TaskComment> {
    const createTaskCommentDto: CreateTaskCommentDto = {
      comment: comment
    };

    return TaskBusinessService._modifying.run(taskId, () =>
      firstValueFrom(this.taskAPIService.createTaskCommentByTaskId(taskId, createTaskCommentDto))
        .then(t => {
          this.notifyUtils.notify(TasksMessages.SUCCESS_COMMENT_ADDED);
          return t;
        })
    );
  }

  deleteCommentOnTask(taskId: number , commentId: number): Promise<TaskComment> {
    return TaskBusinessService._modifying.run(taskId, () =>
      firstValueFrom(this.taskAPIService.deleteTaskCommentByTaskIdAndCommentId(taskId, commentId))
        .then(t => {
          this.notifyUtils.notify(TasksMessages.SUCCESS_COMMENT_DELETED);
          return t;
        })
    );
  }

  getTasks(
    title: string,
    creatorIds: number[],
    ownerIds: number[],
    statuses: TaskStatus[],
    alignmentTypes: TaskAlignmentType[],
    alignmentIds: number[],
    archived: boolean | null
  ): Observable<Task[]> {
    return this.taskAPIService.getTasks(title, creatorIds, ownerIds, statuses, alignmentTypes, alignmentIds, archived);
  }

  getTaskActivity(taskId: number): Observable<TaskActivity[]> {
    return this.taskAPIService.getTaskActivity(taskId);
  }

  getTaskComments(taskId: number): Observable<TaskComment[]> {
    return this.taskAPIService.getTaskComments(taskId);
  }

  getOwnedTasksWithPositionAlignments(): Observable<Task[]> {
    return this.taskAPIService.getOwnedTasksWithPositionAlignments();
  }
}